// Supervisor → Customer Memory (D-24b / U-3).
//
// Review and REMOVE what an agent has learned about a customer: search a
// profile, see its long-term memories (type · importance · recall · source
// conversation · date), delete one (🗑) or clear all (a data-deletion request
// or a wrong fact). Memories are written automatically at conversation close —
// this surface only reviews + removes them; enabling memory itself lives on the
// agent (Overview → Long-term memory). Backend: /api/identity (supervisor-gated).

import { useState, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import { identityApi } from "../../api/client";

const DIM = COLORS.textDim || "#9090a8";
const MUTED = COLORS.textMuted || "#6b7280";
const BORDER = COLORS.border || "#e2e5eb";
const ACCENT = COLORS.accent || "#3b82f6";
const RED = COLORS.red || "#ef4444";
const ACCENT_SOFT = COLORS.accentSoft || "#e7ecff";
const ACCENT_TEXT = COLORS.accentText || ACCENT;
const SURFACE_HOVER = COLORS.surfaceHover || "#f7f9fc";

// ── pure helpers (unit-testable) ─────────────────────────
const TYPE_TONES = {
  fact: "#1e34c9", preference: "#0f766e", issue: "#b3261e",
  context: "#6d4a00", instruction: "#5b21b6",
};
export function memoryTypeTone(type) {
  return TYPE_TONES[(type || "").toLowerCase()] || MUTED;
}
export function importancePct(importance) {
  const n = Number(importance);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n * 100)));
}
export function summarizeMemories(memories) {
  const list = Array.isArray(memories) ? memories : [];
  const prefs = list.filter((m) => (m.type || "").toLowerCase() === "preference").length;
  const avg = list.length
    ? (list.reduce((s, m) => s + (Number(m.importance) || 0), 0) / list.length)
    : 0;
  const oldest = list.reduce((min, m) => {
    const t = m.created_at ? Date.parse(m.created_at) : NaN;
    return Number.isFinite(t) && (min === null || t < min) ? t : min;
  }, null);
  return {
    total: list.length,
    preferences: prefs,
    avgImportance: list.length ? avg.toFixed(2) : "—",
    oldest: oldest ? new Date(oldest).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—",
  };
}

function fmtDate(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
}

const card = { background: COLORS.surface, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 18px" };

export default function CustomerMemoryTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);     // profile search results (or null)
  const [selected, setSelected] = useState(null);   // chosen profile
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    const term = q.trim();
    if (term.length < 2) { setError("Type at least 2 characters."); return; }
    setLoading(true); setError(null); setResults(null); setSelected(null); setMemories([]);
    try {
      const r = await identityApi.searchProfiles(term);
      setResults((r && r.data) || []);
    } catch { setError("Search failed."); }
    finally { setLoading(false); }
  }, [q]);

  const pick = useCallback(async (profile) => {
    setSelected(profile); setResults(null); setLoading(true); setError(null);
    try {
      const r = await identityApi.getMemories(profile.id);
      setMemories((r && r.data) || []);
    } catch { setError("Couldn't load memories."); }
    finally { setLoading(false); }
  }, []);

  const clearSelection = () => { setSelected(null); setMemories([]); };

  const deleteOne = useCallback(async (m) => {
    if (!window.confirm("Delete this memory? This cannot be undone.")) return;
    try {
      await identityApi.deleteMemory(m.id);
      setMemories((prev) => prev.filter((x) => x.id !== m.id));
    } catch { setError("Delete failed."); }
  }, []);

  const deleteAll = useCallback(async () => {
    if (!selected) return;
    if (!window.confirm("Delete ALL memories for this customer? This cannot be undone.")) return;
    try {
      await identityApi.deleteAllMemories(selected.id);
      setMemories([]);
    } catch { setError("Delete failed."); }
  }, [selected]);

  const stats = summarizeMemories(memories);
  const chipName = selected ? (selected.name || selected.email || selected.phone || selected.id) : "";
  const chipSub = selected ? (selected.email || selected.phone) : "";
  const chipSubEl = chipSub ? <span style={{ opacity: 0.7 }}>· {chipSub}</span> : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 860 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Customer memory</div>
        <p style={{ margin: "4px 0 0", color: DIM, fontSize: 13 }}>
          Review and remove what an agent has learned about a customer — e.g. a data-deletion request or a wrong fact.
        </p>
      </div>

      {/* search / selection */}
      <div style={card}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DIM }}>Customer</div>
        {selected ? (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: ACCENT_SOFT, color: ACCENT_TEXT, border: `1px solid ${BORDER}`, borderRadius: 20, padding: "5px 12px", fontSize: 13, fontWeight: 600 }}>{chipName}{chipSubEl}<button type="button" aria-label="Clear customer" onClick={clearSelection} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", opacity: 0.7, fontSize: 13, padding: 0 }}>✕</button></span></div>
        ) : (
          <form onSubmit={search} style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a customer by name, email or phone…" style={{ flex: 1, minWidth: 220, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "9px 12px", background: COLORS.surfaceHover || "#f7f9fc", color: COLORS.text, fontSize: 14, outline: "none" }} />
            <button type="submit" disabled={loading} style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: loading ? "wait" : "pointer" }}>Search</button>
          </form>
        )}
        {error && <div style={{ color: RED, fontSize: 12.5, marginTop: 8 }}>{error}</div>}

        {/* search results */}
        {results && !selected && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            {results.length === 0 ? (
              <div style={{ color: DIM, fontSize: 13 }}>No customers match “{q}”.</div>
            ) : results.map((p) => (
              <button key={p.id} type="button" onClick={() => pick(p)} style={{ textAlign: "left", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "9px 12px", background: COLORS.surfaceHover || "#f7f9fc", cursor: "pointer", color: COLORS.text }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.name || "(no name)"}</div><div style={{ fontSize: 12, color: DIM }}>{[p.email, p.phone].filter(Boolean).join(" · ") || p.id}</div></button>
            ))}
          </div>
        )}

        {/* stats for the selected customer */}
        {selected && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 14 }}>
            {[
              { n: stats.total, l: "Memories" },
              { n: stats.preferences, l: "Preferences" },
              { n: stats.avgImportance, l: "Avg importance" },
              { n: stats.oldest, l: "Oldest" },
            ].map((s, i) => (
              <div key={i} style={{ background: COLORS.surfaceHover || "#f7f9fc", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: COLORS.text }}>{s.n}</div>
                <div style={{ fontSize: 11, color: DIM, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* memory list */}
      {selected && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>What the agent remembers</div>
            {memories.length > 0 && (
              <button type="button" onClick={deleteAll}
                style={{ border: `1px solid ${RED}`, color: RED, background: "transparent", borderRadius: 8,
                  padding: "6px 11px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Delete all</button>
            )}
          </div>

          {loading ? (
            <div style={{ color: DIM, fontSize: 13 }}>Loading…</div>
          ) : memories.length === 0 ? (
            <div style={{ color: DIM, fontSize: 13 }}>No memories stored for this customer.</div>
          ) : memories.map((m) => {
            const tone = memoryTypeTone(m.type);
            const recalled = m.access_count ?? 0;
            return (<div key={m.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", border: `1px solid ${BORDER}`, borderRadius: 11, padding: "12px 13px", background: COLORS.surfaceHover || "#f7f9fc", marginBottom: 9 }}><span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", padding: "2px 8px", borderRadius: 6, color: tone, background: `${tone}1e`, whiteSpace: "nowrap" }}>{m.type || "memory"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: COLORS.text }}>{m.content}</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", color: DIM, fontSize: 11.5, marginTop: 6 }}>
                    {m.source_conversation_id && <span>from conv <span style={{ fontFamily: "monospace" }}>#{String(m.source_conversation_id).slice(0, 8)}</span></span>}
                    <span>recalled {recalled}×</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>importance <span style={{ width: 46, height: 5, borderRadius: 3, background: BORDER, overflow: "hidden", display: "inline-block" }}><span style={{ display: "block", height: "100%", width: `${importancePct(m.importance)}%`, background: ACCENT }} /></span></span>
                    <span>{fmtDate(m.created_at)}</span>
                  </div>
                </div>
                <button type="button" aria-label="Delete this memory" title="Delete this memory" onClick={() => deleteOne(m)} style={{ border: `1px solid ${BORDER}`, background: COLORS.surface, color: DIM, borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 14, flex: "none" }}>🗑</button>
              </div>
            );
          })}

          <div style={{ fontSize: 12, color: DIM, background: SURFACE_HOVER, border: `1px dashed ${BORDER}`, borderRadius: 8, padding: "9px 11px", marginTop: 6 }}>Memories are written automatically when a conversation ends — this screen only reviews and removes them. Enabling memory itself lives on the agent (Overview → Long-term memory).</div>
        </div>
      )}
    </div>
  );
}
